import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  IdCard, Plus, Trash2, X, FileUp, ExternalLink, ChevronRight, ChevronDown, Save,
} from "lucide-react";
import api, { API } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const CERT_TYPES = [
  { v: "formation_tpmr", l: "Formation TPMR" },
  { v: "cerfa_prefectoral", l: "Cerfa préfectoral" },
  { v: "habilitation", l: "Habilitation" },
  { v: "recyclage", l: "Recyclage" },
  { v: "autre", l: "Autre" },
];
const COLOR = { rouge: "bg-red-500", orange: "bg-amber-400", vert: "bg-emerald-500", gris: "bg-slate-300" };
const STATUT = { valide: "Validé", en_attente: "En attente", expire: "Expiré" };

export default function Drivers() {
  const { user } = useAuth();
  const canWrite = user && (user.role === "admin" || user.role === "editor");
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [nd, setNd] = useState({ nom: "", prenom: "", telephone: "", email: "" });
  const [certForm, setCertForm] = useState(null); // {driverId, type, intitule, date_obtention, date_echeance, periodicite_mois, statut}

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/drivers");
      setDrivers(data);
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const createDriver = async () => {
    if (!nd.nom.trim()) return toast.error("Nom requis");
    try {
      await api.post("/drivers", nd);
      setShowNew(false);
      setNd({ nom: "", prenom: "", telephone: "", email: "" });
      await load();
      toast.success("Conducteur ajouté");
    } catch { toast.error("Échec"); }
  };

  const removeDriver = async (id) => {
    if (!window.confirm("Supprimer ce conducteur ?")) return;
    await api.delete(`/drivers/${id}`);
    await load();
  };

  const saveCert = async () => {
    const f = certForm;
    const payload = {
      type: f.type, intitule: f.intitule, date_obtention: f.date_obtention || "",
      date_echeance: f.date_echeance || "", statut: f.statut || "en_attente",
      periodicite_mois: f.periodicite_mois ? Number(f.periodicite_mois) : null,
    };
    try {
      if (f.id) await api.put(`/drivers/${f.driverId}/certifications/${f.id}`, payload);
      else await api.post(`/drivers/${f.driverId}/certifications`, payload);
      setCertForm(null);
      await load();
      toast.success("Certification enregistrée");
    } catch { toast.error("Échec"); }
  };

  const deleteCert = async (driverId, cid) => {
    await api.delete(`/drivers/${driverId}/certifications/${cid}`);
    await load();
  };

  const uploadDoc = async (driverId, cid, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/drivers/${driverId}/certifications/${cid}/document`, fd);
      await load();
      toast.success("Document ajouté");
    } catch (err) { toast.error(err?.response?.data?.detail || "Échec de l'envoi"); }
  };

  return (
    <div className="ml-64 p-8 min-h-screen" data-testid="drivers-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight">Conducteurs</h1>
          <p className="text-slate-500 font-medium mt-1">
            {drivers.length} conducteur(s) — formations TPMR, Cerfa, habilitations & recyclages
          </p>
        </div>
        {canWrite && (
          <div className="flex items-center gap-3">
            <a
              data-testid="export-certs-excel"
              href={`${API}/drivers/export/excel`}
              className="flex items-center gap-2 bg-[#4ADE80] font-bold px-4 py-2.5 rounded-xl nb-btn text-sm"
            >
              <FileUp strokeWidth={2.5} size={18} /> Excel
            </a>
            <a
              data-testid="export-certs-pdf"
              href={`${API}/drivers/export/pdf`}
              className="flex items-center gap-2 bg-white font-bold px-4 py-2.5 rounded-xl nb-btn text-sm"
            >
              <FileUp strokeWidth={2.5} size={18} /> PDF
            </a>
            <button
              data-testid="add-driver-button"
              onClick={() => setShowNew(true)}
              className="flex items-center gap-2 bg-[#4F46E5] text-white font-bold px-5 py-2.5 rounded-xl nb-btn"
            >
              <Plus strokeWidth={2.5} size={20} /> Ajouter
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <p className="text-slate-400 py-16 text-center font-medium">Chargement...</p>
      ) : drivers.length === 0 ? (
        <div className="nb-card p-12 text-center" data-testid="drivers-empty">
          <IdCard className="mx-auto text-slate-300 mb-3" size={48} />
          <p className="font-heading font-black text-2xl mb-2">Aucun conducteur</p>
          <p className="text-slate-500 font-medium">Ajoutez vos conducteurs pour suivre leurs certifications.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {drivers.map((d) => (
            <div key={d.id} className="nb-card overflow-hidden" data-testid={`driver-${d.id}`}>
              <div className="p-5 flex items-center gap-4">
                <span className={`w-3 h-3 rounded-full border-2 border-slate-900 ${COLOR[d.alerte_couleur]}`} title="Statut certifications" />
                <button
                  onClick={() => setOpen(open === d.id ? null : d.id)}
                  className="flex-1 flex items-center gap-3 text-left"
                  data-testid={`driver-toggle-${d.id}`}
                >
                  {open === d.id ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  <div>
                    <p className="font-heading font-black text-lg">{d.prenom} {d.nom}</p>
                    <p className="text-slate-500 text-sm font-medium">
                      {d.certifications.length} certification(s)
                      {d.telephone ? ` · ${d.telephone}` : ""}
                    </p>
                  </div>
                </button>
                {canWrite && (
                  <>
                    <button
                      data-testid={`add-cert-${d.id}`}
                      onClick={() => setCertForm({ driverId: d.id, type: "formation_tpmr", intitule: "", date_obtention: "", date_echeance: "", periodicite_mois: "", statut: "valide" })}
                      className="flex items-center gap-1.5 bg-[#FBBF24] font-bold px-3 py-2 rounded-lg nb-btn text-sm"
                    >
                      <Plus size={16} strokeWidth={2.5} /> Certification
                    </button>
                    <button onClick={() => removeDriver(d.id)} className="text-red-500 p-2" data-testid={`delete-driver-${d.id}`}>
                      <Trash2 size={18} />
                    </button>
                  </>
                )}
              </div>

              {open === d.id && (
                <div className="border-t-2 border-slate-100 p-5 space-y-2 bg-slate-50">
                  {d.certifications.length === 0 && (
                    <p className="text-slate-400 font-medium text-sm py-2">Aucune certification enregistrée.</p>
                  )}
                  {d.certifications.map((c) => (
                    <div key={c.id} className="flex items-center gap-3 bg-white border-2 border-slate-900 rounded-lg p-3" data-testid={`cert-${c.id}`}>
                      <span className={`w-2.5 h-2.5 rounded-full ${COLOR[c.couleur]}`} />
                      <div className="flex-1 min-w-0">
                        <p className="font-bold">{c.type_label}{c.intitule ? ` — ${c.intitule}` : ""}</p>
                        <p className="text-slate-500 text-xs font-medium">
                          {c.date_echeance ? `Échéance ${c.date_echeance}` : "Sans échéance"}
                          {c.days_until != null && (c.days_until < 0 ? ` · expirée depuis ${Math.abs(c.days_until)} j` : ` · dans ${c.days_until} j`)}
                          {c.periodicite_mois ? ` · recyclage / ${c.periodicite_mois} mois` : ""}
                        </p>
                      </div>
                      <span className="text-xs font-bold px-2 py-1 rounded bg-slate-200">{STATUT[c.statut] || c.statut}</span>
                      {c.has_document && (
                        <a
                          href={`${API}/drivers/${d.id}/certifications/${c.id}/document`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-indigo-600 text-sm font-bold inline-flex items-center gap-1"
                          data-testid={`cert-doc-${c.id}`}
                        >
                          <ExternalLink size={14} /> Doc
                        </a>
                      )}
                      {canWrite && (
                        <>
                          <label className="text-slate-600 cursor-pointer p-1" title="Joindre un PDF" data-testid={`cert-upload-${c.id}`}>
                            <FileUp size={16} />
                            <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => uploadDoc(d.id, c.id, e)} />
                          </label>
                          <button
                            onClick={() => setCertForm({ driverId: d.id, id: c.id, type: c.type, intitule: c.intitule || "", date_obtention: c.date_obtention || "", date_echeance: c.date_echeance || "", periodicite_mois: c.periodicite_mois || "", statut: c.statut })}
                            className="text-slate-600 text-sm font-bold"
                          >
                            Modifier
                          </button>
                          <button onClick={() => deleteCert(d.id, c.id)} className="text-red-500 p-1"><Trash2 size={16} /></button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* New driver modal */}
      {showNew && (
        <Modal title="Nouveau conducteur" onClose={() => setShowNew(false)} testid="driver-modal">
          {[["Nom *", "nom"], ["Prénom", "prenom"], ["Téléphone", "telephone"], ["Email", "email"]].map(([l, k]) => (
            <Field key={k} label={l}>
              <input
                data-testid={`driver-${k}`}
                value={nd[k]}
                onChange={(e) => setNd({ ...nd, [k]: e.target.value })}
                className="nb-input w-full px-3 py-2.5"
              />
            </Field>
          ))}
          <button data-testid="driver-save" onClick={createDriver} className="mt-2 w-full bg-[#4F46E5] text-white font-bold py-3 rounded-xl nb-btn flex items-center justify-center gap-2">
            <Save size={18} /> Enregistrer
          </button>
        </Modal>
      )}

      {/* Certification modal */}
      {certForm && (
        <Modal title={certForm.id ? "Modifier la certification" : "Nouvelle certification"} onClose={() => setCertForm(null)} testid="cert-modal">
          <Field label="Type">
            <select data-testid="cert-type" value={certForm.type} onChange={(e) => { const t = e.target.value; setCertForm({ ...certForm, type: t, ...(t === "recyclage" ? { date_echeance: "" } : { periodicite_mois: "" }) }); }} className="nb-input w-full px-3 py-2.5">
              {CERT_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
          </Field>
          <Field label="Intitulé">
            <input data-testid="cert-intitule" value={certForm.intitule} onChange={(e) => setCertForm({ ...certForm, intitule: e.target.value })} className="nb-input w-full px-3 py-2.5" />
          </Field>
          <Field label="Date d'obtention">
            <input type="date" data-testid="cert-obtention" value={certForm.date_obtention} onChange={(e) => setCertForm({ ...certForm, date_obtention: e.target.value })} className="nb-input w-full px-3 py-2.5" />
          </Field>
          {certForm.type === "recyclage" ? (
            <Field label="Périodicité (mois) — recalcule l'échéance">
              <input type="number" data-testid="cert-periodicite" value={certForm.periodicite_mois} onChange={(e) => setCertForm({ ...certForm, periodicite_mois: e.target.value })} placeholder="ex. 60" className="nb-input w-full px-3 py-2.5" />
            </Field>
          ) : (
            <Field label="Date d'échéance">
              <input type="date" data-testid="cert-echeance" value={certForm.date_echeance} onChange={(e) => setCertForm({ ...certForm, date_echeance: e.target.value })} className="nb-input w-full px-3 py-2.5" />
            </Field>
          )}
          <Field label="Statut">
            <select data-testid="cert-statut" value={certForm.statut} onChange={(e) => setCertForm({ ...certForm, statut: e.target.value })} className="nb-input w-full px-3 py-2.5">
              <option value="valide">Validé</option>
              <option value="en_attente">En attente</option>
              <option value="expire">Expiré</option>
            </select>
          </Field>
          <button data-testid="cert-save" onClick={saveCert} className="mt-2 w-full bg-[#4F46E5] text-white font-bold py-3 rounded-xl nb-btn flex items-center justify-center gap-2">
            <Save size={18} /> Enregistrer
          </button>
        </Modal>
      )}
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="mb-3">
    <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">{label}</label>
    <div className="mt-1.5">{children}</div>
  </div>
);

const Modal = ({ title, onClose, children, testid }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center px-6 bg-black/40" data-testid={testid}>
    <div className="w-full max-w-md bg-white border-2 border-slate-900 rounded-2xl p-6 relative max-h-[90vh] overflow-y-auto">
      <button onClick={onClose} className="absolute top-4 right-4 text-slate-400" data-testid="modal-close"><X size={20} /></button>
      <h2 className="font-heading font-black text-2xl mb-5">{title}</h2>
      {children}
    </div>
  </div>
);
